import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Collapse, Dropdown, Menu } from "antd";
import { PlusOutlined, DownOutlined, TableOutlined, BarChartOutlined, MinusCircleOutlined } from "@ant-design/icons";
import Axios from "axios";
import jwt from "jsonwebtoken";
import moment from "moment";
import ResourceList from "./ResourceList";
import ResourceView from "./ResourceView";
import ResourceFilters from "./ResourceFilters";
import LoadingOverlay from "../../../components/LoadingOverlay/LoadingOverlay";
import { getResourcesAsync } from "../../../redux/resourceSlice";
import {
  addResouece,
  setResourceType,
  removeResource,
  updateResourceSource,
  updateSelectedResource,
  updateSelectedFields,
  reOrderFields,
} from "../../../redux/editorSlice";
import { useParams } from "react-router";

const { Panel } = Collapse;

export default function ResourceEditor(props) {
  const [selectedResource, setSelectedResource] = useState({});
  const [reportDataStatus, setReportDataStatus] = useState("default");
  const [recordId, setRecordId] = useState(null);
  const dispatch = useDispatch();
  const { selectedId } = useParams();
  const resources = useSelector((state) => state.resources);
  const editorState = useSelector((state) => state.editor);
  const [loadingFields, setLoadingFields] = useState(false);

  // Prefer the redux copy: availableFields / reportData are written there once the
  // resource's data arrives, while the local snapshot is taken at selection time and
  // goes stale - which left the Builder and Grouping field dropdowns empty.
  const activeSelectedResource = editorState.selectedResource?.id
    ? editorState.selectedResource
    : selectedResource || {};

  const fetchResources = useCallback(() => {
    dispatch(getResourcesAsync());
  }, [dispatch]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  // useEffect(() => {
  //   if (selectedId && recordId !== selectedId) {
  //     setSelectedResource(editorState.selectedResource);
  //   }
  // }, [selectedId, editorState.selectedResource]);

  useEffect(() => {
    if (selectedId && recordId !== selectedId && editorState.selectedResource) {
      setSelectedResource(editorState.selectedResource);
    }
  }, [selectedId, recordId, editorState.selectedResource]);

  /* Every board starts with one resource block, so the panel is never an empty box with
     nothing to act on. It matters most for a report: that holds a single resource, so the
     Add button is hidden and without this there would be no way to create the first one.
     Waits for a board being edited to finish loading, or it would add a spurious resource
     before the saved ones arrive. */
  useEffect(() => {
    if (editorState.isLoadingDashboard) return;
    if ((editorState.resourceList || []).length === 0) {
      dispatch(addResouece("Rest API"));
    }
  }, [editorState.isLoadingDashboard, editorState.resourceList, dispatch]);

  // const handleAddResource = (resourceType) => {
  //   console.log("addNewResource", resourceType);
  //   dispatch(addResouece(resourceType));
  // };

  /* The reducer builds the resource (with the next free id) and selects it. This used to
     build its own object with a hardcoded id of 1 and select that, so the panels stayed
     on the first resource after adding a second one. */
  const handleAddResource = (resourceType) => {
    dispatch(addResouece(resourceType));
  };

  // changes an existing block's API Type instead of adding another resource
  const handleChangeResourceType = (id, type) => {
    dispatch(setResourceType({ id, type }));
  };

  const getSelectedConnection = async (data) => {
    if (typeof data !== "object") {
      // the active resource, so a change made inside one block cannot write to another
      let sellectedRes = JSON.parse(JSON.stringify(activeSelectedResource || selectedResource || {}));
      // read before the line below overwrites it - `data` is the id being picked
      const previousSourceId = sellectedRes?.source?.id;
      const sourceChanged = previousSourceId != null && previousSourceId !== data;
      sellectedRes.source = resources.resourceData.find((r) => r.id === data);
      /* Columns describe the source they were derived from, so a genuine swap starts
         from none. Leaving them meant the panel kept showing the previous source's
         columns until fresh data replaced them - and if the new source returned nothing,
         or the request failed (the catch in fetchReportData dispatches nothing at all),
         they were never replaced and simply stayed.

         Re-picking the SAME source is not a swap: that keeps the configured columns and
         only re-checks them, as before. */
      const updatedResource = {
        ...sellectedRes,
        availableFields: sourceChanged
          ? []
          : sellectedRes?.availableFields?.map((af) => ({
              ...af,
              checked: true,
            })),
        allFields: sourceChanged ? [] : sellectedRes?.allFields,
      };
      setSelectedResource(updatedResource);
      dispatch(updateSelectedResource(updatedResource));
      await fetchReportData(updatedResource);
    }
  };

  const handleSetSelectedResource = async (data) => {
    console.log("selectedResourcesss", data);

    setSelectedResource(data);
    dispatch(updateSelectedResource(data));
  };

  async function fetchReportData(resource = selectedResource) {
    if (resource && resource.source && resource.source.resoueceType === "Rest API") {
      setLoadingFields(true);
      try {
        const requestValues = resource.source.connectionData.requestValues;
        const authInfo = resource.source.connectionData.authInfo;
        let urlParamSring = "";
        if (requestValues.urlParams.length > 0) {
          urlParamSring = urlParamSring + "?";
          for (let i = 0; i < requestValues.urlParams.length; i++) {
            if (requestValues.urlParams[i]["key"] !== "" && requestValues.urlParams[i]["value"] !== "") {
              urlParamSring =
                urlParamSring + requestValues.urlParams[i]["key"] + "=" + requestValues.urlParams[i]["value"] + "&";
            }
          }
          urlParamSring = urlParamSring.slice(0, -1);
        }

        let headerObj = {};
        if (requestValues.requestHeaders.length > 0) {
          for (let i = 0; i < requestValues.requestHeaders.length; i++) {
            if (requestValues.requestHeaders[i]["key"] !== "" && requestValues.requestHeaders[i]["value"] !== "") {
              headerObj[requestValues.requestHeaders[i]["key"]] = requestValues.requestHeaders[i]["value"];
            }
          }
        }

        let bodyParamObj = {};
        if (requestValues.bodyParams.length > 0) {
          for (let i = 0; i < requestValues.bodyParams.length; i++) {
            if (requestValues.bodyParams[i]["key"] !== "" && requestValues.bodyParams[i]["value"] !== "") {
              bodyParamObj[requestValues.bodyParams[i]["key"]] = requestValues.bodyParams[i]["value"];
            } else if (
              requestValues.bodyParams[i]["key"] !== "" &&
              requestValues.bodyParams[i]["dataType"] === "date"
            ) {
              if (requestValues.bodyParams[i]["dateConfig"]) {
                let dateCf = requestValues.bodyParams[i]["dateConfig"];
                if (dateCf.dateOption === "daysBefore") {
                  let dateVal = moment.utc().subtract(dateCf.timeUnits, "day").startOf("day").format(dateCf.dateFormat);
                  bodyParamObj[requestValues.bodyParams[i]["key"]] = dateVal;
                } else if (dateCf.dateOption === "today") {
                  let dateVal = moment().format(dateCf.dateFormat);
                  bodyParamObj[requestValues.bodyParams[i]["key"]] = dateVal;
                }
              }
            }
          }
        }

        setReportDataStatus("pending");
        if (authInfo.method === "jwt") {
          let userToken = localStorage.getItem("accessToken");
          const timestamp = (new Date().getTime() + 1000 * 60 * 60 * 24 * 7) / 1000;
          const privateKey = authInfo.parameters.privateKey;
          var payload = {
            aud: authInfo.parameters.aud,
            exp: timestamp,
            iss: authInfo.parameters.iss,
            userToken: userToken,
          };
          var signOptions = { algorithm: "RS256" };
          var privateKeyVal = privateKey ? privateKey.replace(/\\n/g, "\n") : null;
          if (privateKeyVal) {
            try {
              var token = jwt.sign(payload, privateKeyVal, signOptions);
              await Axios({
                method: requestValues.httpMethod,
                url: resource.source.connectionData.baseUrl + urlParamSring,
                data: JSON.parse(JSON.stringify(bodyParamObj)),
                headers: {
                  "Access-Control-Allow-Origin": "*",
                  "Content-Type": "application/json",
                  Authorization: `Bearer ` + token,
                },
              })
                .then((response) => {
                  setReportDataStatus("success");
                  const reportData = response.data ? response.data : response;
                  dispatch(updateResourceSource(Object.assign({}, resource, { reportData: reportData })));
                })
                .catch(() => setReportDataStatus("error"));
            } catch (e) {
              setReportDataStatus("error");
            }
          }
        } else {
          let httpReqConfig = {
            method: resource.source.connectionData.requestValues.httpMethod,
            url: resource.source.connectionData.baseUrl + urlParamSring,
          };
          if (requestValues.bodyParams.length > 0) httpReqConfig.data = bodyParamObj;
          if (requestValues.requestHeaders.length > 0) httpReqConfig.headers = headerObj;
          // awaited so the `finally` below (and the loading overlay) waits for the response
          await Axios(httpReqConfig)
            .then((response) => {
              setReportDataStatus("success");
              const reportData = response.data ? response.data : response;
              dispatch(updateResourceSource(Object.assign({}, resource, { reportData: reportData })));
            })
            .catch(() => setReportDataStatus("error"));
        }
      } catch (error) {
        setReportDataStatus("error");
      } finally {
        setLoadingFields(false);
      }
    }
  }

  function handleUpdateSelectedFields(data) {
    dispatch(updateSelectedFields(data));
  }

  function handleReOrderFields(data) {
    dispatch(reOrderFields(data));
  }

  function handleRemoveResourceItem(item) {
    dispatch(removeResource(item));
    /* The local snapshot still holds the resource that was just deleted, and
       activeSelectedResource falls back to it whenever redux has no id - which put the
       deleted resource back on screen. Clear it and let redux's choice win. */
    setSelectedResource({});
  }

  // The sections to show in the accordion — only when a resource is selected
  // const hasSelectedResource = selectedResource && selectedResource.id;
  const hasSelectedResource = Boolean(activeSelectedResource?.source?.id);

  return (
    <div className="left-sidebar-inner">
      {/* blocks the whole screen while a newly picked resource loads its rows/columns */}
      {loadingFields ? <LoadingOverlay text="Loading resource data..." /> : null}

      {/* ── CONFIGURATION SECTIONS (only when resource selected) ── */}

      <Collapse defaultActiveKey={["resource"]} className="left-sidebar-collapse" ghost>
        <Panel header="Resources" key="resource">
          {/* One block per resource, stacked. A dashboard can hold several; a report is a
              single widget, so it keeps a single resource. Clicking a block makes it the
              active one - every other panel (Raw Data, Filters, Builder, Chart, Columns,
              Drill Down) is already bound to the active resource, so they follow along. */}
          <div className="resource-stack">
            {(editorState.resourceList || []).map((res, index) => {
              const isActive = res.id === activeSelectedResource?.id;
              // combined resources are configured in place, so only expand the active one
              const isCollapsedCombined = res.type === "Combined Resource" && !isActive;

              return (
                <div
                  key={`res_block_${res.id}`}
                  className={`resource-block${isActive ? " resource-block--active" : ""}`}
                  // select before any control inside handles its own event
                  onMouseDownCapture={() => {
                    if (!isActive) handleSetSelectedResource(res);
                  }}
                >
                  <div className="resource-block-head">
                    <span className="resource-block-index">Resource {index + 1}</span>
                    {(editorState.resourceList || []).length > 1 ? (
                      <button
                        className="resource-block-remove"
                        title="Remove this resource"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveResourceItem(res);
                        }}
                      >
                        <MinusCircleOutlined />
                      </button>
                    ) : null}
                  </div>

                  {isCollapsedCombined ? (
                    <div className="resource-block-collapsed">Combined Resource — click to configure</div>
                  ) : (
                    <ResourceView
                      section="resource"
                      resourceList={editorState.resourceList ? editorState.resourceList : []}
                      addNewResource={handleAddResource}
                      onChangeResourceType={(type) => handleChangeResourceType(res.id, type)}
                      removeResourceItem={handleRemoveResourceItem}
                      connections={resources.resourceData}
                      connectionType={res.type}
                      selectedResource={isActive ? activeSelectedResource : res}
                      dashboardConfig={editorState.dashboardConfig ? editorState.dashboardConfig : {}}
                      getSelectedConnection={getSelectedConnection}
                      reportDataObject={editorState.selectedResource?.reportData}
                      reportDataStatus={reportDataStatus}
                      updateSelectedFields={handleUpdateSelectedFields}
                      activeWidgetName={props.activeWidgetName}
                      setSelected={handleSetSelectedResource}
                      selected={activeSelectedResource}
                    />
                  )}
                </div>
              );
            })}

            {/* Reports hold one widget, so they hold one resource - say so instead of
                just leaving the button out */}
            {editorState.dashboardConfig?.boardType !== "reports" ? (
              <button className="resource-add-btn" onClick={() => handleAddResource("Rest API")}>
                Add another Resource
              </button>
            ) : (
              <div className="resource-single-note">A report uses a single resource.</div>
            )}
          </div>
        </Panel>
        {hasSelectedResource ? (
          <>
            <Panel header="Raw Data" key="rawdata">
              <ResourceView
                section="rawdata"
                resourceList={editorState.resourceList ? editorState.resourceList : []}
                addNewResource={handleAddResource}
                removeResourceItem={handleRemoveResourceItem}
                connections={resources.resourceData}
                connectionType={activeSelectedResource?.type}
                selectedResource={activeSelectedResource}
                dashboardConfig={editorState.dashboardConfig ? editorState.dashboardConfig : {}}
                getSelectedConnection={getSelectedConnection}
                reportDataObject={editorState.selectedResource?.reportData}
                reportDataStatus={reportDataStatus}
                updateSelectedFields={handleUpdateSelectedFields}
                activeWidgetName={props.activeWidgetName}
                setSelected={handleSetSelectedResource}
                selected={activeSelectedResource}
              />
            </Panel>

            <Panel header="Filters" key="filters">
              <ResourceView
                section="filters"
                resourceList={editorState.resourceList ? editorState.resourceList : []}
                addNewResource={handleAddResource}
                removeResourceItem={handleRemoveResourceItem}
                connections={resources.resourceData}
                connectionType={activeSelectedResource?.type}
                selectedResource={activeSelectedResource}
                dashboardConfig={editorState.dashboardConfig ? editorState.dashboardConfig : {}}
                getSelectedConnection={getSelectedConnection}
                reportDataObject={editorState.selectedResource?.reportData}
                reportDataStatus={reportDataStatus}
                updateSelectedFields={handleUpdateSelectedFields}
                activeWidgetName={props.activeWidgetName}
                setSelected={handleSetSelectedResource}
                selected={activeSelectedResource}
              />
            </Panel>

            <Panel header="Builder" key="builder">
              <ResourceView
                section="builder"
                resourceList={editorState.resourceList ? editorState.resourceList : []}
                addNewResource={handleAddResource}
                removeResourceItem={handleRemoveResourceItem}
                connections={resources.resourceData}
                connectionType={activeSelectedResource?.type}
                selectedResource={activeSelectedResource}
                dashboardConfig={editorState.dashboardConfig ? editorState.dashboardConfig : {}}
                getSelectedConnection={getSelectedConnection}
                reportDataObject={editorState.selectedResource?.reportData}
                reportDataStatus={reportDataStatus}
                updateSelectedFields={handleUpdateSelectedFields}
                activeWidgetName={props.activeWidgetName}
                setSelected={handleSetSelectedResource}
                selected={activeSelectedResource}
              />
            </Panel>

            <Panel header={props.activeWidgetName === "chart" ? "Chart" : "Grouping"} key="grouping">
              <ResourceView
                section="grouping"
                resourceList={editorState.resourceList ? editorState.resourceList : []}
                addNewResource={handleAddResource}
                removeResourceItem={handleRemoveResourceItem}
                connections={resources.resourceData}
                connectionType={activeSelectedResource?.type}
                selectedResource={activeSelectedResource}
                dashboardConfig={editorState.dashboardConfig ? editorState.dashboardConfig : {}}
                getSelectedConnection={getSelectedConnection}
                reportDataObject={editorState.selectedResource?.reportData}
                reportDataStatus={reportDataStatus}
                updateSelectedFields={handleUpdateSelectedFields}
                activeWidgetName={props.activeWidgetName}
                setSelected={handleSetSelectedResource}
                selected={activeSelectedResource}
                activeChartType={props.activeChartType}
              />
            </Panel>

            {/* <Panel header="HyperLink" key="hyperlink">
              <ResourceView
                section="hyperlink"
                connections={resources.resourceData}
                connectionType={selectedResource.type}
                selectedResource={editorState.selectedResource ? editorState.selectedResource : {}}
                dashboardConfig={editorState.dashboardConfig ? editorState.dashboardConfig : {}}
                getSelectedConnection={getSelectedConnection}
                reportDataObject={editorState.selectedResource?.reportData}
                reportDataStatus={reportDataStatus}
                updateSelectedFields={handleUpdateSelectedFields}
                activeWidgetName={props.activeWidgetName}
                setSelected={handleSetSelectedResource}
                selected={selectedResource}
              />
            </Panel> */}
          </>
        ) : (
          <div className="left-sidebar-empty">
            <p>Select or create a resource to configure it.</p>
          </div>
        )}
      </Collapse>
    </div>
  );
}
